// src/components/SmartFlow/nodes/node/Condition/ConditionConfig.jsx

import React, { useState, useEffect } from "react";
import "./ConditionConfig.css";

const ConditionConfig = ({
  config,
  nodeMetadata,
  onChange,
  validationErrors = {},
  inputData,
}) => {
    console.log("input dara in ConditionConfig:", inputData);
  const [localConfig, setLocalConfig] = useState(config || {});
  const [activeTab, setActiveTab] = useState("parameters");
  const DEFAULT_CONDITION_TYPE = "simple";
  const DEFAULT_OPERATOR = "equals";

  useEffect(() => {
    setLocalConfig(config || {});
    }, [config]);

        useEffect(() => {
        if (!localConfig) return;

        const next = { ...localConfig };

        if (!next.condition_type) next.condition_type = DEFAULT_CONDITION_TYPE;

        if (next.condition_type === "simple") {
            if (next.operator == null) next.operator = DEFAULT_OPERATOR;
            if (next.value1 == null) next.value1 = "";
            if (next.value2 == null) next.value2 = "";
        }

        if (next.condition_type === "multiple") {
            if (!next.combine_operation) next.combine_operation = "AND";
            if (!Array.isArray(next.conditions)) next.conditions = [];
        }

        const changed = JSON.stringify(next) !== JSON.stringify(localConfig);
        if (changed) {
            setLocalConfig(next);
            onChange(next);
        }
    }, [localConfig?.condition_type]);


  const schema = nodeMetadata?.config_schema || {};

  const updateConfig = (field, value) => {
    const newConfig = {
      ...localConfig,
      [field]: value,
    };
    setLocalConfig(newConfig);
    onChange(newConfig);
  };

  const updateCondition = (index, field, value) => {
    const conditions = [...(localConfig.conditions || [])];
    conditions[index] = {
      ...conditions[index],
      [field]: value,
    };
    updateConfig('conditions', conditions);
  };

  const addCondition = () => {
    const conditions = [...(localConfig.conditions || [])];
    conditions.push({
      value1: '',
      operator: 'equals',
      value2: '',
    });
    updateConfig('conditions', conditions);
  };

  const removeCondition = (index) => {
    const conditions = [...(localConfig.conditions || [])];
    conditions.splice(index, 1);
    updateConfig('conditions', conditions);
  };

  const hasError = (field) => Boolean(validationErrors[field]);

  const renderError = (field) =>
    hasError(field) ? (
      <div className="condition-config__error-text">
        {validationErrors[field]}
      </div>
    ) : null;

  const operatorOptions = [
    { value: 'equals', label: 'Equals (=)' },
    { value: 'not_equals', label: 'Not Equals (≠)' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Not Contains' },
    { value: 'starts_with', label: 'Starts With' },
    { value: 'ends_with', label: 'Ends With' },
    { value: 'greater_than', label: 'Greater Than (>)' },
    { value: 'less_than', label: 'Less Than (<)' },
    { value: 'greater_or_equal', label: 'Greater or Equal (≥)' },
    { value: 'less_or_equal', label: 'Less or Equal (≤)' },
    { value: 'is_empty', label: 'Is Empty' },
    { value: 'is_not_empty', label: 'Is Not Empty' },
    { value: 'regex_match', label: 'Regex Match' },
  ];

  const renderConditionTypeSelector = () => {
    const conditionType = localConfig.condition_type || 'simple';
    return (
      <div className="condition-config__field">
        <label className="condition-config__label">
          Condition Type
          <span className="condition-config__label-required">*</span>
        </label>
        <select
          className="condition-config__input"
          value={conditionType}
          onChange={(e) => updateConfig('condition_type', e.target.value)}
        >
          <option value="simple">Simple (Single Condition)</option>
          <option value="multiple">Multiple (AND/OR)</option>
          <option value="expression">Custom Expression</option>
          <option value="always_true">Always True</option>
          <option value="always_false">Always False</option>
        </select>
        {renderError('condition_type')}
      </div>
    );
  };

  const renderSimpleCondition = () => {
    return (
      <div className="condition-config__simple-condition">
        <div className="condition-config__field">
          <label className="condition-config__label">
            Value 1
            <span className="condition-config__help-text">
            {' '}(supports expressions like {'{{ trigger.email }}'})
            </span>

          </label>
          <input
            type="text"
            className="condition-config__input"
            value={localConfig.value1 || ''}
            placeholder="e.g., {{ trigger.status }}"
            onChange={(e) => updateConfig('value1', e.target.value)}
          />
          {renderError('value1')}
        </div>

        <div className="condition-config__field">
          <label className="condition-config__label">Operator</label>
          <select
            className="condition-config__input"
            value={localConfig.operator || 'equals'}
            onChange={(e) => updateConfig('operator', e.target.value)}
          >
            {operatorOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {renderError('operator')}
        </div>

        {!['is_empty', 'is_not_empty'].includes(localConfig.operator) && (
          <div className="condition-config__field">
            <label className="condition-config__label">
              Value 2
              <span className="condition-config__help-text">
                {' '}(supports expressions)
              </span>
            </label>
            <input
              type="text"
              className="condition-config__input"
              value={localConfig.value2 || ''}
              placeholder="e.g., 'approved'"
              onChange={(e) => updateConfig('value2', e.target.value)}
            />
            {renderError('value2')}
          </div>
        )}

        <div className="condition-config__info-box">
          <i className="bi bi-info-circle"></i>
          <div>
            <strong>Expression Syntax:</strong> Use {'{{ ... }}'} for dynamic values.
            Examples: {'{{ trigger.email }}'}, {'{{ variables.count }}'}, {'{{ nodes.http_1.output.status }}'}
          </div>
        </div>
      </div>
    );
  };

  const renderMultipleConditions = () => {
    const conditions = localConfig.conditions || [];
    const combineOperation = localConfig.combine_operation || 'AND';

    return (
      <div className="condition-config__multiple-conditions">
        <div className="condition-config__field">
          <label className="condition-config__label">Combine Operation</label>
          <select
            className="condition-config__input"
            value={combineOperation}
            onChange={(e) => updateConfig('combine_operation', e.target.value)}
          >
            <option value="AND">AND (all must be true)</option>
            <option value="OR">OR (at least one must be true)</option>
          </select>
        </div>

        <div className="condition-config__conditions-list">
          <div className="condition-config__conditions-header">
            <span>Conditions</span>
            <button
              type="button"
              className="condition-config__button condition-config__button--primary"
              onClick={addCondition}
            >
              <i className="bi bi-plus-lg"></i> Add Condition
            </button>
          </div>

          {conditions.length === 0 && (
            <div className="condition-config__empty-state">
              <i className="bi bi-inbox"></i>
              <p>No conditions yet. Click "Add Condition" to get started.</p>
            </div>
          )}

          {conditions.map((condition, index) => (
            <div key={index} className="condition-config__condition-item">
              <div className="condition-config__condition-header">
                <span className="condition-config__condition-number">
                  Condition {index + 1}
                </span>
                <button
                  type="button"
                  className="condition-config__button condition-config__button--danger-small"
                  onClick={() => removeCondition(index)}
                  title="Remove condition"
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>

              <div className="condition-config__condition-fields">
                <div className="condition-config__field-inline">
                  <label className="condition-config__label-small">Value 1</label>
                  <input
                    type="text"
                    className="condition-config__input-small"
                    value={condition.value1 || ''}
                    placeholder="{{ trigger.status }}"
                    onChange={(e) => updateCondition(index, 'value1', e.target.value)}
                  />
                </div>

                <div className="condition-config__field-inline">
                  <label className="condition-config__label-small">Operator</label>
                  <select
                    className="condition-config__input-small"
                    value={condition.operator || 'equals'}
                    onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                  >
                    {operatorOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                  <div className="condition-config__field-inline">
                    <label className="condition-config__label-small">Value 2</label>
                    <input
                      type="text"
                      className="condition-config__input-small"
                      value={condition.value2 || ''}
                      placeholder="'approved'"
                      onChange={(e) => updateCondition(index, 'value2', e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderExpressionCondition = () => {
    return (
      <div className="condition-config__expression-condition">
        <div className="condition-config__field">
          <label className="condition-config__label">
            Expression
            <span className="condition-config__label-required">*</span>
            <span className="condition-config__help-text">
              {' '}(must return true or false)
            </span>
          </label>
          <textarea
            className="condition-config__textarea"
            value={localConfig.expression || ''}
            placeholder="e.g., {{ trigger.age > 18 and trigger.status == 'active' }}"
            rows={4}
            onChange={(e) => updateConfig('expression', e.target.value)}
          />
          {renderError('expression')}
        </div>

        <div className="condition-config__info-box">
          <i className="bi bi-lightbulb"></i>
          <div>
            <strong>Jinja2 Expression:</strong> Write a custom expression that evaluates to true/false.
            <br />
            Examples:
            <ul>
              <li>{'{{ trigger.age >= 18 }}'}</li>
              <li>{'{{ variables.count > 10 and trigger.status == "active" }}'}</li>
              <li>{'{{ nodes.http_1.output.code == 200 }}'}</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderStaticCondition = () => {
    const conditionType = localConfig.condition_type;
    return (
      <div className="condition-config__static-condition">
        <div className="condition-config__info-box condition-config__info-box--large">
          <i className="bi bi-check-circle"></i>
          <div>
            <strong>Static Condition: {conditionType === 'always_true' ? 'Always True' : 'Always False'}</strong>
            <p>
              This condition will {conditionType === 'always_true' ? 'always' : 'never'} evaluate to true.
              {conditionType === 'always_true'
                ? ' Useful for testing or forcing a specific branch.'
                : ' Useful for disabling a branch temporarily.'}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderParametersTab = () => {
    const conditionType = localConfig.condition_type || 'simple';

    return (
      <div className="condition-config__tab-content">
        {renderConditionTypeSelector()}

        <div className="condition-config__divider"></div>

        {conditionType === 'simple' && renderSimpleCondition()}
        {conditionType === 'multiple' && renderMultipleConditions()}
        {conditionType === 'expression' && renderExpressionCondition()}
        {(conditionType === 'always_true' || conditionType === 'always_false') && renderStaticCondition()}
      </div>
    );
  };

  const renderSettingsTab = () => {
    return (
      <div className="condition-config__tab-content">
        <div className="condition-config__info-box">
          <i className="bi bi-gear"></i>
          <div>
            <strong>Condition Settings</strong>
            <p>
              This node branches your workflow based on the condition result:
            </p>
            <ul>
              <li><strong>True branch:</strong> Connect to the "if_true" output handle</li>
              <li><strong>False branch:</strong> Connect to the "if_false" output handle</li>
            </ul>
            <p>
              The condition result (true/false) will be passed to the next nodes in the workflow context.
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="condition-config">
      <div className="condition-config__tabs">
        <button
          className={`condition-config__tab ${activeTab === 'parameters' ? 'condition-config__tab--active' : ''}`}
          onClick={() => setActiveTab('parameters')}
        >
          <i className="bi bi-sliders"></i> Parameters
        </button>
        <button
          className={`condition-config__tab ${activeTab === 'settings' ? 'condition-config__tab--active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <i className="bi bi-gear"></i> Settings
        </button>
      </div>

      <div className="condition-config__tab-panels">
        {activeTab === 'parameters' && renderParametersTab()}
        {activeTab === 'settings' && renderSettingsTab()}
      </div>
    </div>
  );
};

export default ConditionConfig;
